import { createNanoEvents, type Emitter } from 'nanoevents';

export enum DocumentStatus {
    /**
     * 文档数据正在加载首屏数据
     *    * 本地文档：数据正在从文件加载首屏数据
     *    * 在线文档：数据正在从服务器加载首屏数据
     */
    LoadingHead = 'LoadingHead',

    /**
     * 文档数据正在加载分片数据
     *    * 本地文档：数据正在从文件加载分片数据
     *    * 在线文档：数据正在从服务器加载分片数据
     */
    LoadingChunk = 'LoadingChunk',

    /**
     * 文档快照发生变更，此状态需要阻断用户关闭文档，提示保存
     */
    Modified = 'Modified',
    /**
     * 数据已同步：
     *    * 本地文档：数据已保存为文件
     *    * 在线文档：本地变更已提交
     *
     * 此状态可以安全关闭文档
     */
    Sync = 'Sync',
    /**
     * 数据保存中：
     *   * 本地文档：数据正在保存为文件
     *   * 在线文档：本地变更提交中
     *
     * 此状态需要阻断用户关闭文档，提示正在保存
     */
    Syncing = 'Syncing',
    /**
     * 文档出现数据损坏，无法继续编辑和保存
     */
    Corrupted = 'Corrupted',
}

export enum ResourceStatus {
    /**
     * 待导入的资源已落盘
     */
    Sync = 'Sync',
    /**
     * 待导入的资源正在导入
     */
    Syncing = 'Syncing',
}

/**
 * 文档和资源状态管理器
 */
export interface IContentStatusManager {
    /**
     * ContentStatusManager 事件通知
     */
    emitter: Emitter<IContentStatusEvents>;

    /**
     * 获取当前文档状态，包括:
     *  * LoadingHead: 文档数据正在加载首屏数据
     *  * LoadingChunk: 文档数据正在加载分片数据
     *  * Modified: 文档快照发生变更，此状态需要阻断用户关闭文档，提示保存
     *  * Sync: 数据已同步，此状态可以安全关闭文档
     *  * Syncing: 文档数据正在同步
     *  * Corrupted: 文档数据加载失败
     */
    getDocumentStatus(): DocumentStatus;
    /**
     * 获取当前资源状态，包括:
     *  * Sync: 资源已同步
     *  * Syncing: 资源正在同步
     */
    getResourceStatus(): ResourceStatus;

    /**
     * 获取文档加载失败的错误
     */
    getCorruptedError(): Error | undefined;

    /**
     * 文档是否加载完成
     */
    isDocumentReady(): boolean;

    /**
     * 等待文档加载完成
     */
    waitForDocumentReady(): Promise<void>;

    /**
     * 文档加载完成后触发回调
     */
    onDocumentReady(callback: () => void): void;

    /**
     * 添加文档同步任务（本地文档为保存文件，在线文档为提交数据）。
     * 正在同步时，新加入的同步任务会阻塞，直到上一个任务完成后才会执行下一个任务。
     * @param task 同步任务
     *
     * @example
     * ```ts
     * manager.addDocumentSyncingTask(async () => {
     *  await saveDocument();
     * });
     */
    addDocumentSyncingTask(task: () => Promise<void>, force?: boolean): Promise<void>;
    /**
     * 添加资源同步任务，
     * @param task 同步任务
     *
     * @example
     * ```ts
     * manager.addResourceSyncingTask(async () => {
     *  await uploadImage();
     * });
     * ```
     */
    addResourceSyncingTask(task: () => Promise<void>): Promise<void>;

    /**
     * 监听文档状态变更
     * @param callback 状态变更回调
     *
     * @returns 取消监听函数
     *
     * @example
     * ```ts
     * const unsubscribe = manager.onDocumentStatusChange((status) => {
     *   console.log(status);
     * });
     *
     * unsubscribe();
     */
    onDocumentStatusChange(callback: (status: DocumentStatus) => void): () => void;
    /**
     * 监听资源状态变更
     * @param callback 状态变更回调
     *
     * @returns 取消监听函数
     *
     * @example
     * ```ts
     * const unsubscribe = manager.onResourceStatusChange((status) => {
     *    console.log(status);
     * });
     *
     * unsubscribe();
     */
    onResourceStatusChange(callback: (status: ResourceStatus) => void): () => void;
}

export const enum ContentStatusEvents {
    /**
     * 通知 ContentStatusManager 首屏数据加载完毕
     */
    LoadHeadSuccess,
    /**
     * 通知 ContentStatusManager 所有数据加载完毕
     */
    LoadAllChunksSuccess,
    /**
     * 通知 ContentStatusManager 加载失败
     */
    LoadError,
    /**
     * 通知 ContentStatusManager 编辑器触发 Undo
     */
    EditorUndo,
    /**
     * 通知 ContentStatusManager 编辑器触发 Redo
     */
    EditorRedo,
    /**
     * 通知 ContentStatusManager 编辑器触发用户编辑
     */
    EditorOperation,

    /**
     * 通知 ContentStatusManager 同步资源开始
     */
    ResourceSyncBegin,
    /**
     * 通知 ContentStatusManager 同步资源结束
     */
    ResourceSyncEnd,
}

const enum ContentStatusInternalEvents {
    /**
     * 文档状态变更通知
     * 用于通知文档状态变更，例如文档加载完成，文档保存完成等
     */
    DocumentStatusChange = 'document-status-change',
    /**
     * 资源状态变更通知
     */
    ResourceStatusChange = 'resource-status-change',
}

export interface IContentStatusEvents {
    [ContentStatusEvents.LoadHeadSuccess]: () => void;
    [ContentStatusEvents.LoadAllChunksSuccess]: () => void;
    [ContentStatusEvents.LoadError]: (error: Error) => void;

    [ContentStatusEvents.EditorUndo]: () => void;
    [ContentStatusEvents.EditorRedo]: () => void;
    [ContentStatusEvents.EditorOperation]: () => void;

    [ContentStatusEvents.ResourceSyncBegin]: () => void;
    [ContentStatusEvents.ResourceSyncEnd]: () => void;

    [ContentStatusInternalEvents.DocumentStatusChange]: (status: DocumentStatus) => void;
    [ContentStatusInternalEvents.ResourceStatusChange]: (status: ResourceStatus) => void;
}

export class ContentStatusManager implements IContentStatusManager {
    private key = 0;

    private documentStatus = DocumentStatus.LoadingHead;
    private resourceStatus = ResourceStatus.Sync;

    private currentDocumentSyncingTask: Promise<void> | null = null;
    private syncingResourceCount = 0;

    private syncStack: number[] = [];
    private syncTop = -1;
    private lastSyncKey = NaN;
    private isSyncInCurrentMicroTaskQueue = false;

    private corruptedError?: Error;

    public constructor(public emitter = createNanoEvents<IContentStatusEvents>()) {
        this.pushSyncKey();
        this.lastSyncKey = this.getSyncKey();
        this.init();
    }

    public getDocumentStatus() {
        return this.documentStatus;
    }

    public getResourceStatus() {
        return this.resourceStatus;
    }

    public getCorruptedError() {
        return this.corruptedError;
    }

    public waitForDocumentReady() {
        return new Promise<void>((resolve) => {
            this.onDocumentReady(() => {
                resolve();
            });
        });
    }

    public isDocumentReady(): boolean {
        return ![DocumentStatus.LoadingHead, DocumentStatus.LoadingChunk, DocumentStatus.Corrupted].includes(
            this.documentStatus,
        );
    }

    public onDocumentReady(callback: () => void): void {
        if (this.isDocumentReady()) {
            callback();
            return;
        }

        const unsubscribe = this.onDocumentStatusChange(() => {
            if (this.isDocumentReady()) {
                callback();
                unsubscribe();
            }
        });
    }

    public async addDocumentSyncingTask(task: () => Promise<void>): Promise<void> {
        if (!this.isDocumentReady()) {
            await this.waitForDocumentReady();
        }

        while (this.documentStatus === DocumentStatus.Syncing && this.currentDocumentSyncingTask) {
            // 文档正在保存，等待保存完成
            await this.currentDocumentSyncingTask;
        }

        if (this.isSyncInCurrentMicroTaskQueue) {
            /**
             * 文档的保存任务会在一个微任务队列里执行，
             * 如果上一个任务结束后，文档已进入到同步状态，那么后续的任务可以跳过。
             *
             * 这里需要注意，虽然文档进入到同步状态，但用户可能删除本地文件。
             * 因此，即使处于同步状态，当用户触发同步时，也需要执行同步，不然文件可能丢失。
             * 所以，会在追加一个宏任务来重置这个状态。
             *
             */
            return;
        }

        const startSyncing = async () => {
            this.setDocumentStatus(DocumentStatus.Syncing);

            if (this.resourceStatus === ResourceStatus.Syncing) {
                // 资源正在同步，等待同步完成
                await this.waitForResourceSync();
            }

            const currentSyncingKey = this.getSyncKey();
            try {
                await task();
                this.lastSyncKey = currentSyncingKey;
            } catch (err) {
                throw err;
            } finally {
                this.currentDocumentSyncingTask = null;
                this.switchSyncStatus();
                if (this.documentStatus === DocumentStatus.Sync) {
                    this.isSyncInCurrentMicroTaskQueue = true;
                    setTimeout(() => {
                        this.isSyncInCurrentMicroTaskQueue = false;
                    });
                }
            }
        };

        this.currentDocumentSyncingTask = startSyncing();
        await this.currentDocumentSyncingTask;
    }

    public async addResourceSyncingTask(task: () => Promise<void>): Promise<void> {
        this.deltaResourceSyncingCount(1);
        task().finally(() => {
            this.deltaResourceSyncingCount(-1);
        });
    }

    public onDocumentStatusChange(callback: (status: DocumentStatus) => void): () => void {
        return this.emitter.on(ContentStatusInternalEvents.DocumentStatusChange, callback);
    }

    public onResourceStatusChange(callback: (status: ResourceStatus) => void): () => void {
        return this.emitter.on(ContentStatusInternalEvents.ResourceStatusChange, callback);
    }

    private init() {
        this.emitter.on(ContentStatusEvents.LoadHeadSuccess, () => this.finishHeadLoading());
        this.emitter.on(ContentStatusEvents.LoadAllChunksSuccess, () => this.finishDocumentLoading());
        this.emitter.on(ContentStatusEvents.LoadError, (error) => this.intoCorrupted(error));
        this.emitter.on(ContentStatusEvents.EditorUndo, this.handleUndo);
        this.emitter.on(ContentStatusEvents.EditorRedo, this.handleRedo);
        this.emitter.on(ContentStatusEvents.EditorOperation, this.handleInput);
        this.emitter.on(ContentStatusEvents.ResourceSyncBegin, () => this.deltaResourceSyncingCount(1));
        this.emitter.on(ContentStatusEvents.ResourceSyncEnd, () => this.deltaResourceSyncingCount(-1));

        return this;
    }

    private async waitForResourceSync() {
        await new Promise<void>((resolve) => {
            const unsubscribe = this.onResourceStatusChange((status) => {
                if (status === ResourceStatus.Sync) {
                    unsubscribe();
                    resolve();
                }
            });
        });
    }

    private intoCorrupted(error: Error) {
        this.assertStatus(DocumentStatus.LoadingHead, DocumentStatus.LoadingChunk);
        this.corruptedError = error;
        this.setDocumentStatus(DocumentStatus.Corrupted);
        return this;
    }

    private setDocumentStatus(status: DocumentStatus) {
        if (this.documentStatus === status) {
            return;
        }

        this.documentStatus = status;
        this.emitter.emit(ContentStatusInternalEvents.DocumentStatusChange, status);
    }

    private setResourceStatus(status: ResourceStatus) {
        if (this.resourceStatus === status) {
            return;
        }

        this.resourceStatus = status;
        this.emitter.emit(ContentStatusInternalEvents.ResourceStatusChange, status);
    }

    private assertStatus(...expectedStatus: DocumentStatus[]) {
        if (!expectedStatus.includes(this.documentStatus)) {
            throw new Error(`Document status is ${this.documentStatus}, but expected ${expectedStatus.join(' or ')}`);
        }
    }

    private switchSyncStatus() {
        if (this.isModified()) {
            this.setDocumentStatus(DocumentStatus.Modified);
        } else {
            this.setDocumentStatus(DocumentStatus.Sync);
        }
    }

    private handleUndo = () => {
        if (this.syncTop === -1) {
            return;
        }
        this.syncTop -= 1;

        if (this.documentStatus !== DocumentStatus.Syncing) {
            this.switchSyncStatus();
        }
    };

    private handleRedo = () => {
        this.syncTop += 1;

        if (this.documentStatus !== DocumentStatus.Syncing) {
            this.switchSyncStatus();
        }
    };

    private handleInput = () => {
        this.syncStack.splice(this.syncTop + 1);
        this.pushSyncKey();

        if (this.documentStatus !== DocumentStatus.Syncing) {
            this.switchSyncStatus();
        }
    };

    private getSyncKey() {
        return this.syncStack[this.syncTop];
    }

    private pushSyncKey() {
        this.syncStack.push(this.key);
        this.key += 1;
        this.syncTop += 1;
    }

    private isModified() {
        return this.getSyncKey() !== this.lastSyncKey;
    }

    private deltaResourceSyncingCount(amount: number) {
        this.syncingResourceCount += amount;
        this.setResourceStatus(this.syncingResourceCount > 0 ? ResourceStatus.Syncing : ResourceStatus.Sync);
    }

    private finishHeadLoading() {
        this.assertStatus(DocumentStatus.LoadingHead);
        this.setDocumentStatus(DocumentStatus.LoadingChunk);
    }

    private finishDocumentLoading() {
        this.assertStatus(DocumentStatus.LoadingChunk, DocumentStatus.LoadingHead, DocumentStatus.Modified);
        this.switchSyncStatus();
    }
}
